import { z } from 'zod';

import { numberWithUnitSchema } from '@sites/entity-core';

import { EButtonHoverType, EButtonStyleType } from '@common/enums';

import { ThemeCssUnit, ThemeFontStyle, ThemeTextTransform } from '../../enums/css.enum';
import { ThemeFontFamilies } from '../../enums/font-families.enum';
import { ThemeFontTags } from '../../enums/font-tags.enum';
import { ThemeColorNames } from '../../enums/color-names.enum';

/**
 * The pieces every theme variant is built from.
 *
 * A theme is the one thing on a page that was never versioned: `draft_theme_settings` is a
 * free-form JSON blob written by whatever the editor happened to hold, and read back by three apps
 * plus `apps/mcp-backend` with a bare `JSON.parse`. Nothing validated it, nothing could migrate it,
 * and its TypeScript types leaned on `Pick<JssStyle, …>` and on Angular component types
 * (`AdvancedRadiusSettings`, `AdvancedBorderSettings`) that a package cannot import.
 *
 * Spelling the fields out here is most of the value: the shapes below say exactly what is persisted,
 * with no `JssStyle` to hide behind.
 */

/**
 * A colour slot, as the theme stores it: a NAME plus the literal it currently resolves to.
 *
 * The name is what entities reference — a block's `backgroundColor: 'primary'` is resolved through
 * this list by `themeColorCss`, which returns a CSS variable for a known name and the string
 * itself for anything else.
 *
 * Narrowed to the two variants' vocabularies, which is the one place a colour name is NOT free-form:
 * an entity may reference any string (an unknown one is simply a literal colour), but a PALETTE
 * ENTRY is one of the five slots the variant defines. Both enums are unioned because a stored theme
 * does not say which variant wrote it until `entitySubType` does.
 */
export const themeColorSchema = z.object({
    name: z.enum(ThemeColorNames),
    value: z.string(),
});

/** `{ index, values }` — a shipped preset a user can pick from. Same shape for all three parts. */
export const themePresetSchema = <T extends z.ZodTypeAny>(item: T) =>
    z.object({
        index: z.string(),
        values: z.array(item),
    });

/**
 * A length — the number and its unit, kept apart.
 *
 * The theme used to store these as CSS strings (`'16px'`, `'140%'`, `'0px'`), which forced every
 * consumer to know how to take them apart: the settings panel ran a whole `converters.ts` pair to
 * `parseInt` on load and re-append the unit on save, and any panel that forgot the suffix wrote a
 * value the browser ignores. Storing `{ value, unit }` moves that knowledge into the data.
 *
 * **`@sites/entity-core`'s declaration, not a second one.** It already models exactly this for
 * sections and columns, down to the same two units. A workspace with two spellings of "a number
 * with a unit" is how the string-vs-number ambiguity this replaced came about in the first place.
 */
export const themeCssSizeSchema = numberWithUnitSchema;

/**
 * A value that may differ per breakpoint.
 *
 * Every part is optional and the whole object may be absent: a font that was never given a tablet
 * override simply has no `tablet`, and consumers fall back to `desktop` — the same rule the old
 * `tabletStyles ?? mobileStyles ?? styles` chain expressed, now stated once per property instead of
 * once per reader.
 */
export const themeResponsiveSchema = <T extends z.ZodTypeAny>(item: T) =>
    z.object({
        desktop: item.optional(),
        tablet: item.optional(),
        mobile: item.optional(),
    });

/**
 * Typography for one tag.
 *
 * **The properties sit at the root, one responsive object each.** They used to live in three sibling
 * bags — `styles`, `tabletStyles`, `mobileStyles` — which put the breakpoint above the property in
 * the tree and made "what is this tag's font size" a three-way lookup with a fallback chain that
 * every reader re-implemented. Inverting it (`fontSize.tablet` instead of `tabletStyles.fontSize`)
 * puts the whole story of one property in one place, which is also the shape the settings panels
 * bind to.
 *
 * `tag` and `font` ARE the enums, unlike a colour `name`. The two vocabularies live in this package
 * (`enums/font-tags.enum.ts`, `enums/font-families.enum.ts`), so a value outside them is not "a
 * theme from a newer host" — it is a value nothing here can render.
 *
 * `fontFamily` is the resolved CSS stack (`'Open Sans, sans-serif'`), not the enum: `font` names
 * which family the user picked, `fontFamily` is what goes into the stylesheet, and the two are
 * deliberately both persisted because the stack for a family has changed before.
 */
export const themeFontSchema = z.object({
    tag: z.enum(ThemeFontTags),
    font: z.enum(ThemeFontFamilies),
    fontFamily: themeResponsiveSchema(z.string()).optional(),
    fontSize: themeResponsiveSchema(themeCssSizeSchema).optional(),
    fontWeight: themeResponsiveSchema(z.number()).optional(),
    fontStyle: themeResponsiveSchema(z.enum(ThemeFontStyle)).optional(),
    lineHeight: themeResponsiveSchema(themeCssSizeSchema).optional(),
    textTransform: themeResponsiveSchema(z.enum(ThemeTextTransform)).optional(),
    letterSpacing: themeResponsiveSchema(themeCssSizeSchema).optional(),
    /**
     * A literal CSS colour, or a palette name (`dark`) that is emitted as `var(--dark-color)` so the
     * text keeps following the theme. Never set on the `button` tag — see `font-variables.const.ts`.
     */
    color: themeResponsiveSchema(z.string()).optional(),
});

/**
 * A colour on a button — always a LIST, never a bare string.
 *
 * The list exists because each colour property maps to a PAIR of CSS variables, a `-light` and a
 * `-dark` one (`config/css-variables/button-variables.const.ts`), and the formatter walks the two in
 * step. Every writer and every shipped preset stores exactly one element, and the formatter falls
 * back to `[0]` for the second variable, so one element is the normal case rather than a degenerate
 * one.
 *
 * **It used to be `string | string[]`, and the `string` half did not render.** The formatter only
 * emits a value when the variable entry and the stored value are BOTH arrays; a bare string matched
 * neither branch, so the CSS variable was simply never set and the button lost that colour with no
 * error anywhere. The preset strip was worse: it indexes `color[0]` unguarded, so `'primary'`
 * became `'p'`. Two silent failures for a spelling nothing in the workspace writes — the same
 * string-or-number ambiguity that `themeCssSizeSchema` replaced, left standing one field longer.
 *
 * The legacy schema still accepts both (`legacy/button.ts`), and the migration lifts a bare string
 * into a one-element list, so a stored theme carrying one still reads — and stops being ambiguous
 * the first time it is saved.
 */
export const themeButtonColorSchema = z.array(z.string());

/**
 * A button's rest state.
 *
 * `borderRadius` and `borderWidth` are `{ value, unit }` for the same reason font sizes are: they
 * used to arrive as `8`, `'8px'` or `'0'` depending on which panel last wrote them, so every reader
 * had to know how to take a length apart — and `'0'` did not say what unit it was zero of.
 */
export const themeButtonDataSchema = z.object({
    borderRadius: themeCssSizeSchema,
    borderStyle: z.string(),
    borderWidth: themeCssSizeSchema,
    borderColor: themeButtonColorSchema,
    color: themeButtonColorSchema,
    background: themeButtonColorSchema,
});

/**
 * The hover state. `hoverType` picks which of the other fields actually applies — `opacity` uses
 * `opacity`, `shadow` uses `shadow`, `color` uses the three colour fields — but all of them are
 * stored regardless, so switching the type back restores what was there.
 */
export const themeButtonHoverSchema = z.object({
    hoverType: z.enum(EButtonHoverType),
    opacity: z.number(),
    shadow: z.string(),
    color: themeButtonColorSchema,
    background: themeButtonColorSchema,
    borderColor: themeButtonColorSchema,
});

/**
 * One button style.
 *
 * `base` is the only rest-state bag. Older panels also wrote `styles` and `data`, both superseded
 * by it; the readers that still wanted them were all in the `simple` variant, which lives in
 * `@libs/themes` now and was never versioned.
 *
 * **A theme button has no contour or corner settings.** It used to declare `isIndependentCorners`,
 * `radiusSettings` and `advancedBorderSettings` — four bare numbers and twelve flat `border*` keys,
 * with no unit, no breakpoint and no version. Nothing ever read them on a theme: the panel writes
 * only `base` and `hover`, no shipped preset carries them, and no CSS variable is emitted from
 * them. If a theme button ever needs a contour it embeds `border` and `borderRadius` from
 * `@sites/entity-core`, which model exactly this and model it better — responsive, with units, and
 * self-versioning, with `isIndependent*` derived from the sides rather than stored beside them.
 *
 * All five fields stay on the LEGACY schema so a stored theme carrying them still parses; the
 * migration drops them.
 */
export const themeButtonSchema = z.object({
    styleType: z.enum(EButtonStyleType),
    base: themeButtonDataSchema.optional(),
    active: themeButtonDataSchema.optional(),
    hover: themeButtonHoverSchema.optional(),
});

/**
 * A selection — which preset the user picked, and the values it currently holds.
 *
 * `selectedIndex` is the preset's id, kept alongside the values so the settings panel can show
 * which swatch is active. The values are NOT derived from it: a user may edit a preset's colours
 * without leaving the preset, and the edit lives here.
 */
export const themeSelectionSchema = <T extends z.ZodTypeAny>(item: T) =>
    z.object({
        selected: z.array(item),
        selectedIndex: z.string().nullable().optional(),
    });

export const themeColorPresetSchema = themePresetSchema(themeColorSchema);
export const themeFontPresetSchema = themePresetSchema(themeFontSchema);
export const themeButtonPresetSchema = themePresetSchema(themeButtonSchema);

export const themeSelectedColorsSchema = themeSelectionSchema(themeColorSchema);
export const themeSelectedFontsSchema = themeSelectionSchema(themeFontSchema).extend({
    /** Whether the fonts panel writes desktop edits through to tablet and mobile. Absent = on. */
    syncWithDesktop: z.boolean().optional(),
});
export const themeSelectedButtonsSchema = themeSelectionSchema(themeButtonSchema);

export type ThemeColor = z.infer<typeof themeColorSchema>;
export type ThemeFont = z.infer<typeof themeFontSchema>;
export type ThemeCssSize = z.infer<typeof themeCssSizeSchema>;
export type ThemeResponsive<T> = { desktop?: T; tablet?: T; mobile?: T };
export type ThemeButton = z.infer<typeof themeButtonSchema>;
export type ThemeButtonData = z.infer<typeof themeButtonDataSchema>;
export type ThemeButtonColor = z.infer<typeof themeButtonColorSchema>;
export type ThemeButtonHover = z.infer<typeof themeButtonHoverSchema>;
export type ThemeColorPreset = z.infer<typeof themeColorPresetSchema>;
export type ThemeFontPreset = z.infer<typeof themeFontPresetSchema>;
export type ThemeButtonPreset = z.infer<typeof themeButtonPresetSchema>;
export type ThemeSelectedColors = z.infer<typeof themeSelectedColorsSchema>;
export type ThemeSelectedFonts = z.infer<typeof themeSelectedFontsSchema>;
export type ThemeSelectedButtons = z.infer<typeof themeSelectedButtonsSchema>;
